import React, { useState, useEffect, useRef, useCallback } from 'react';
import { T } from '../shared/constants';
import { Badge, DifficultyBadge, Card, ProgressBar, MultilineText } from '../shared/ui';
import { QuestionBody, ReportQuestionButton } from './questionParts';
import { answerText } from './naplanItems';

// Chrome shared by the composed-response NAPLAN items (multi, order, match,
// hottext, short, inline). Layout and styling mirror MCQRenderer exactly so the
// item types read as one product.

const FONT = "'Plus Jakarta Sans', sans-serif";

/** Header badges, progress bar, passage and question card (with practice hint). */
// Reading items on wide screens: the passage on the left (sticky, scrolls on its own), the question and answers on the
// right, so a child can read and answer without scrolling up and down between them. Narrow screens keep one column.
export const PASSAGE_LAYOUT_CSS = `
@media (min-width:1100px){
  .lumio-item.has-passage{max-width:1240px !important;display:grid;grid-template-columns:minmax(0,1.15fr) minmax(0,1fr);column-gap:28px;align-items:start}
  .lumio-item.has-passage > .lumio-span{grid-column:1/-1}
  .lumio-item.has-passage > .lumio-passage{position:sticky;top:12px;max-height:calc(100vh - 190px);overflow-y:auto;margin-bottom:0 !important;font-size:14px !important}
}`;

export function ItemFrame({ question, state, isAnswered, children }) {
  const { mode, current, total } = state;
  return (
    <div className={question.passage ? "lumio-item has-passage" : "lumio-item"} style={{ animation: "fadeUp .3s ease", padding: "24px 32px 16px", maxWidth: 860, margin: "0 auto", width: "100%" }}>
      <style>{PASSAGE_LAYOUT_CSS}</style>
      <div className="lumio-span" style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, flexWrap: "wrap" }}>
        <Badge label={`Q ${current} / ${total}`} color={T.textMid} />
        <Badge label={question.topic} color={T.accent} />
        <DifficultyBadge level={question.difficulty} />
        {mode !== "exam" && question.criteria && <Badge label={question.criteria.split("—")[0].trim()} color={T.purple} />}
        <div style={{ marginLeft: "auto", flexShrink: 0 }}>
          <ReportQuestionButton question={question} />
        </div>
      </div>

      <div className="lumio-span"><ProgressBar value={current} max={total} height={3} style={{ marginBottom: 24 }} /></div>

      {question.passage && (
        <div className="lumio-passage" style={{ background: T.surface, border: `1px solid ${T.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 20, fontSize: 13, color: T.textMid, lineHeight: 1.7, whiteSpace: "pre-line" }}>
          {question.passage}
        </div>
      )}

      <div>
      <Card style={{ marginBottom: 20, padding: "24px" }}>
        <QuestionBody text={question.q} />
        {mode === "practice" && !isAnswered && question.hint && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${T.border}` }}>
            <p style={{ fontSize: 12, color: T.textDim, marginBottom: 6, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Hint</p>
            <p style={{ fontSize: 13, color: T.textMid, lineHeight: 1.6 }}>💡 {question.hint}</p>
          </div>
        )}
      </Card>
      {children}
      </div>
    </div>
  );
}

/** Small uppercase instruction line above the interaction ("Select two", "Drag to order"). */
export function Instruction({ children }) {
  return (
    <p style={{ fontSize: 12, color: T.textDim, margin: "0 0 10px", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>{children}</p>
  );
}

export function CheckButton({ disabled, onClick, mode }) {
  return (
    <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        style={{
          padding: "12px 28px", borderRadius: 12, border: "none",
          background: disabled ? T.border : T.accent, color: disabled ? T.textMid : "white",
          fontWeight: 700, fontSize: 15, cursor: disabled ? "not-allowed" : "pointer", fontFamily: FONT,
          transition: "all .15s",
        }}
      >
        {mode === "exam" ? "Submit answer" : "Check answer"}
      </button>
    </div>
  );
}

/** Stored, item-specific feedback on a wrong response (replaces the AI diagnosis). */
export function StoredFeedback({ message, hint }) {
  if (!message) return null;
  return (
    <div data-testid="stored-feedback" style={{ marginTop: 12, marginBottom: 12, animation: 'fadeUp 0.4s ease' }}>
      <div style={{ background: `${T.red}10`, border: `1px solid ${T.red}33`, borderRadius: 10, padding: '10px 14px', marginBottom: hint ? 8 : 0 }}>
        <p style={{ fontSize: 11, color: T.red, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>What happened</p>
        <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.55 }}>{message}</p>
      </div>
      {hint && (
        <div style={{ background: `${T.gold}10`, border: `1px solid ${T.gold}33`, borderRadius: 10, padding: '10px 14px' }}>
          <p style={{ fontSize: 11, color: T.gold, fontWeight: 700, margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Remember next time</p>
          <p style={{ fontSize: 13, color: T.text, margin: 0, lineHeight: 1.55 }}>💡 {hint}</p>
        </div>
      )}
    </div>
  );
}

/** Correct/incorrect card with explanation and stored feedback — same as MCQRenderer's. */
export function ResultCard({ question, mode, isAnswered, isCorrect, showExplanation, feedback, onAskLumi, onExplainVideo }) {
  return (
    <>
      {showExplanation && (
        <Card style={{ marginBottom: 20, background: isCorrect ? T.greenLo : T.redLo, borderColor: isCorrect ? T.green : T.red, animation: "scaleIn .3s ease", padding: "20px" }}>
          <p role="status" style={{ fontWeight: 700, color: isCorrect ? T.green : T.red, marginBottom: 8, fontSize: 15 }}>
            {isCorrect ? "✓ Correct!" : "✗ Incorrect"}
          </p>
          {!isCorrect && isAnswered && (
            <p data-testid="correct-answer" style={{ fontSize: 14, color: T.text, margin: "0 0 10px", lineHeight: 1.6 }}>
              <span style={{ fontWeight: 700 }}>Correct answer: </span>{answerText(question)}
            </p>
          )}
          <MultilineText text={question.explanation} style={{ color: T.text, fontSize: 14, lineHeight: 1.7, marginBottom: mode === "practice" ? 14 : 0 }} />
          {!isCorrect && isAnswered && <StoredFeedback message={feedback} hint={question.hint} />}
          {mode === "practice" && (
            <div style={{ paddingTop: 12, borderTop: `1px solid ${isCorrect ? T.green + "33" : T.red + "33"}` }}>
              <p style={{ fontSize: 12, color: T.textDim, marginBottom: 4, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>Assessment Criteria</p>
              <p style={{ fontSize: 13, color: T.textMid }}>{question.criteria || ""}</p>
            </div>
          )}
        </Card>
      )}
    </>
  );
}

/** Colours for a part after marking: 'right' | 'wrong' | 'missed' | 'selected' | null. */
export function partColors(status) {
  if (status === 'right')    return { bg: T.greenLo, border: T.green, color: T.green };
  if (status === 'wrong')    return { bg: T.redLo,   border: T.red,   color: T.red };
  if (status === 'missed')   return { bg: T.card,    border: T.green, color: T.green };
  if (status === 'selected') return { bg: T.accentLo, border: T.accent, color: T.accentDk };
  return { bg: T.card, border: T.border, color: T.text };
}

/**
 * Pointer-event drag (mouse, pen AND touch — HTML5 drag-and-drop does not fire
 * on iPad). Drop targets are elements carrying `data-drop-key`; the key under
 * the pointer on release is passed to onDrop(dragId, key). A press that never
 * moves past the threshold is left to the element's own onClick (tap/keyboard
 * path); `consumeClick(id)` swallows the one click a browser may fire on the
 * dragged element after a real drag.
 */
export function usePointerDrag({ onDrop, disabled }) {
  const [drag, setDrag] = useState(null);
  const live = useRef(null);
  const lastDrag = useRef({ id: null, at: 0 });
  const cbs = useRef({ onDrop });
  cbs.current = { onDrop };
  const detach = useRef(null);

  const keyAt = (x, y) => {
    if (typeof document === 'undefined' || typeof document.elementFromPoint !== 'function') return null;
    const el = document.elementFromPoint(x, y);
    const t = el && el.closest ? el.closest('[data-drop-key]') : null;
    return t ? t.getAttribute('data-drop-key') : null;
  };

  useEffect(() => () => { if (detach.current) detach.current(); }, []);

  const start = useCallback((id, label, e) => {
    if (disabled) return;
    if (e.pointerType === 'mouse' && e.button !== undefined && e.button !== 0) return;
    if (detach.current) detach.current();
    live.current = { id, label, x0: e.clientX, y0: e.clientY, x: e.clientX, y: e.clientY, moved: false, over: null };
    const move = ev => {
      const s = live.current; if (!s) return;
      s.x = ev.clientX; s.y = ev.clientY;
      if (!s.moved && Math.hypot(s.x - s.x0, s.y - s.y0) > 6) s.moved = true;
      if (s.moved) {
        if (ev.cancelable && ev.preventDefault) ev.preventDefault();
        s.over = keyAt(s.x, s.y);
        setDrag({ id: s.id, label: s.label, x: s.x, y: s.y, over: s.over });
      }
    };
    const end = (ev, cancelled) => {
      const s = live.current;
      if (detach.current) detach.current();
      live.current = null;
      setDrag(null);
      if (!s || !s.moved) return;
      lastDrag.current = { id: s.id, at: Date.now() };
      if (cancelled) return;
      const x = ev && typeof ev.clientX === 'number' ? ev.clientX : s.x;
      const y = ev && typeof ev.clientY === 'number' ? ev.clientY : s.y;
      const key = keyAt(x, y) ?? s.over;
      if (key != null && cbs.current.onDrop) cbs.current.onDrop(s.id, key);
    };
    const up = ev => end(ev, false);
    const cancel = ev => end(ev, true);
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', cancel);
    detach.current = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', cancel);
      detach.current = null;
    };
  }, [disabled]);

  const consumeClick = useCallback(id => {
    const ld = lastDrag.current;
    if (ld.id === id && Date.now() - ld.at < 400) { lastDrag.current = { id: null, at: 0 }; return true; }
    return false;
  }, []);

  return { drag, start, consumeClick };
}

/** Floating label that follows the pointer while dragging (never intercepts hit-testing). */
export function DragGhost({ drag }) {
  if (!drag) return null;
  return (
    <div aria-hidden="true" style={{
      position: 'fixed', left: drag.x, top: drag.y, transform: 'translate(-50%, -120%)',
      pointerEvents: 'none', zIndex: 300, background: T.card, border: `1.5px solid ${T.accent}`,
      borderRadius: 10, padding: '8px 12px', fontSize: 14, fontWeight: 600, color: T.text,
      boxShadow: '0 8px 24px rgba(0,0,0,0.15)', fontFamily: FONT, maxWidth: 260,
    }}>{drag.label}</div>
  );
}
